import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined'
import { Button, Input, Skeleton } from 'antd'
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

interface NotesEditorProps {
  value: string | undefined
  setValue: Dispatch<SetStateAction<string | undefined>>
  label?: ReactNode
  isLoading?: boolean
}

export const NotesEditor: React.FC<NotesEditorProps> = ({
  value,
  setValue,
  label,
  isLoading = false,
}) => {
  const resolvedValue = value ?? ''
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = event.target.value
      setValue(nextValue)
    },
    [setValue]
  )

  const areaId = useMemo(() => v4(), [])

  return (
    <NotesEditorContainer>
      <NotesLabel>
        {label ?? 'Notes'}
        <NotesInputWrapper>
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <>
              <StyledTextArea
                id={areaId}
                rows={6}
                value={resolvedValue}
                onChange={handleChange}
              />
              <ClearNotesButton
                type='text'
                size='small'
                disabled={value === undefined || value === ''}
                onClick={() => setValue('')}
                icon={<CloseCircleOutlined />}
                aria-label='Clear notes'
              />
            </>
          )}
        </NotesInputWrapper>
      </NotesLabel>
    </NotesEditorContainer>
  )
}

const NotesEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 12px 12px;
`

const NotesLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
`

const NotesInputWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledTextArea = styled(Input.TextArea)`
  resize: vertical;
  padding-right: 36px;
  width: 100%;
`

const ClearNotesButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`
